import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import {
    BookmarkPlus,
    Building2,
    Check,
    ExternalLink,
    Globe,
    LayoutGrid,
    List,
    Loader2,
    Lock,
    Music,
    Plus,
    SearchX,
    Share2,
    Sparkles,
} from "lucide-react";

import PageMeta from "../../../../shared/ui/common/PageMeta";
import Button from "../../../../shared/ui/button/Button";
import { useCenter } from "../../../center";
import {
    useCenterMeditations,
    useCopyMeditation,
    useMeditations,
    usePublicMeditations,
    useUnpublishMeditation,
} from "../../application/useMeditations";
import { MEDITATIONS_PAGE_SIZE, Meditation, MeditationFilters } from "../../domain/types";
import MeditationCard from "../components/MeditationCard";
import MeditationRow from "../components/MeditationRow";
import MeditationFilterBar from "../components/MeditationFilterBar";
import MeditationPager from "../components/MeditationPager";
import MeditationPreview from "../components/MeditationPreview";
import MeditationPublishForm from "../components/MeditationPublishForm";
import { type MeditationMenuItem } from "../components/MeditationActionsMenu";
import MeditationShareSheet from "../components/MeditationShareSheet";
import MeditationTrackLibrary from "../components/MeditationTrackLibrary";
import SectionTabs from "../../../../shared/ui/tabs/SectionTabs";

type Tab = "mine" | "public" | "center";
// Inside each catalogue: its meditations, or its audio tracks.
type Section = "meditations" | "tracks";
// How a catalogue is laid out: cards to browse, or a list to scan titles.
type Layout = "cards" | "list";

const LAYOUT_KEY = "alchely.meditations.layout";

/** The layout this therapist last chose, if they ever chose one. */
function readStoredLayout(): Layout | null {
    try {
        const stored = localStorage.getItem(LAYOUT_KEY);
        return stored === "list" || stored === "cards" ? stored : null;
    } catch {
        // A browser that refuses storage gets each catalogue's default.
        return null;
    }
}

/**
 * The meditation library, as three catalogues.
 *
 * Mine: the therapist's own meditations, published or not, and their own
 * music. Public: the public library exactly as everyone else sees it — their
 * own published work included, because the honest way to check how yours reads
 * out there is to find it where the others are — and the music therapists
 * chose to share. The centre's, for a therapist in one: what its
 * administrators put there, meditations and music both.
 *
 * Each catalogue carries its meditations and its music together, rather than
 * the music on a tab of its own: a therapist looking at their centre's
 * catalogue wants to see everything the centre offers, not half of it.
 *
 * The tab lives in the URL so a therapist who found something in the public
 * library can send the link to a colleague, and so coming back from an editor
 * lands where they left. An old link to the music tab lands on their own.
 */
export default function MeditationsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const requested = searchParams.get("tab");
    // A therapist in a centre also has the centre's library, which every
    // member uses and only its administrators fill.
    const { center, isAdmin } = useCenter();
    const inCenter = Boolean(center);
    const tab: Tab =
        requested === "public" || (requested === "center" && inCenter) ? requested : "mine";
    // An old link to the music tab lands on the therapist's own tracks.
    const section: Section =
        searchParams.get("section") === "tracks" || requested === "music" ? "tracks" : "meditations";

    const [filters, setFilters] = useState<MeditationFilters>({});
    /*
     * Ten at a time, in every catalogue: a library is scanned, and a hundred
     * cards are scrolled past rather than read. Back to the first page
     * whenever what is listed changes.
     */
    const [page, setPage] = useState(1);
    const changeFilters = (next: MeditationFilters) => {
        setFilters(next);
        setPage(1);
    };
    /*
     * Which meditation is being handed out, if any. Held here rather than in
     * the card so that closing the sheet cannot be confused with closing the
     * card it was opened from.
     */
    const [sharing, setSharing] = useState<{ id: string; title: string } | null>(
        null
    );

    /*
     * One choice for every catalogue: a therapist who prefers to scan titles
     * prefers it in their own library and in the public one alike.
     *
     * Until they choose, the public catalogue opens as a list — it is the long
     * one, written by many people, where you run your eye down titles — and
     * their own and the centre's as cards.
     */
    const [chosenLayout, setChosenLayout] = useState<Layout | null>(readStoredLayout);
    const layout: Layout = chosenLayout ?? (tab === "public" ? "list" : "cards");
    const selectLayout = (next: Layout) => {
        setChosenLayout(next);
        try {
            localStorage.setItem(LAYOUT_KEY, next);
        } catch {
            // Still switches; it just asks again next session.
        }
    };

    // Their own library has one author, so looking by author is for the others;
    // a name typed there must not quietly empty this one.
    // Only the catalogue on screen is fetched: the public one pages through a
    // library everyone writes, and nobody asked for it from their own tab.
    const showingMeditations = section === "meditations";
    const mine = useMeditations(
        { ...filters, author: undefined },
        showingMeditations && tab === "mine"
    );
    const published = usePublicMeditations(
        filters,
        page,
        showingMeditations && tab === "public"
    );
    const centerLibrary = useCenterMeditations(
        filters,
        showingMeditations && tab === "center"
    );
    const copyMeditation = useCopyMeditation();
    const unpublishMeditation = useUnpublishMeditation();
    // The meditation being published from the list, asked in place under it.
    const [publishing, setPublishing] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    // Someone else's meditation, open over the catalogue.
    const [previewing, setPreviewing] = useState<string | null>(null);

    const active = tab === "mine" ? mine : tab === "center" ? centerLibrary : published;
    // Everything the catalogue loaded: the whole of their own and their
    // centre's, one page of the public one.
    const loaded = useMemo(() => active.data ?? [], [active.data]);
    const totalPages =
        tab === "public"
            ? undefined
            : Math.max(1, Math.ceil(loaded.length / MEDITATIONS_PAGE_SIZE));
    // A page left behind by a removal shows the last one there is.
    const currentPage = totalPages ? Math.min(page, totalPages) : page;
    const meditations = useMemo(
        () =>
            tab === "public"
                ? loaded
                : loaded.slice(
                      (currentPage - 1) * MEDITATIONS_PAGE_SIZE,
                      currentPage * MEDITATIONS_PAGE_SIZE
                  ),
        [tab, loaded, currentPage]
    );
    const hasNext = tab === "public" ? published.hasNext : currentPage < (totalPages ?? 1);
    const goToPage = (next: number) => {
        setPage(next);
        // The next ten start at the top, not where the last ten ended.
        document.getElementById("meditation-results")?.scrollIntoView?.({ block: "start" });
    };

    /*
     * Words the loaded meditations are filed under that no catalogue has, so
     * the filter can offer them back rather than making the therapist
     * remember exactly how they typed it.
     */
    const ownLabels = useMemo(
        () => ({
            therapyTypes: [
                ...new Set(loaded.flatMap((m) => m.custom_therapy_types ?? [])),
            ],
            problems: [
                ...new Set(
                    loaded.flatMap((m) => m.custom_presenting_problems ?? [])
                ),
            ],
        }),
        [loaded]
    );

    // A therapist who bookmarked this page and later dropped the tool gets a
    // 403. "Could not load" would send them looking for an outage; the honest
    // answer is that the tool is off.
    const notAdopted =
        (active.error as { response?: { status?: number } })?.response?.status === 403;

    const selectTab = (next: Tab) => {
        const params = new URLSearchParams(searchParams);
        params.set("tab", next);
        // Written out, not left implied: an old `?tab=music` link reaches the
        // tracks only through `tab`, which this just replaced.
        params.set("section", section);
        setSearchParams(params, { replace: true });
        setPage(1);
    };

    const selectSection = (next: Section) => {
        const params = new URLSearchParams(searchParams);
        params.set("tab", tab);
        params.set("section", next);
        setSearchParams(params, { replace: true });
        setPage(1);
    };

    const copy = async (id: string) => {
        setActionError(null);
        try {
            const created = await copyMeditation.mutateAsync(id);
            navigate(`/meditations/${created.id}`);
        } catch {
            setPreviewing(null);
            setActionError(t("meditation.copyFailed"));
        }
    };

    /*
     * Publishing straight from their own library, as with their music:
     * withdrawing is at once, publishing asks whose it is and on what terms.
     * Only there — the public catalogue is for reading, and a centre's
     * meditation stays in the centre.
     */
    const togglePublished = (meditation: Meditation) => {
        setActionError(null);
        if (meditation.visibility !== "public") {
            setPublishing(meditation.id);
            return;
        }
        unpublishMeditation
            .mutateAsync(meditation.id)
            .catch(() => setActionError(t("meditation.publish.withdrawFailed")));
    };

    const publishFormFor = (meditation: Meditation) =>
        publishing === meditation.id && meditation.visibility !== "public" ? (
            <MeditationPublishForm
                meditation={meditation}
                onDone={() => setPublishing(null)}
            />
        ) : null;

    // Keyed on whose it is, not on which tab it is in: the public library
    // shows the therapist their own published work too, and you do not take a
    // copy of your own.
    const isTheirs = (meditation: Meditation) =>
        Boolean(meditation.is_owner || meditation.in_center_library);

    /*
     * The whole entry opens it. Their own and their centre's go to the page
     * where they are worked on; someone else's opens over the catalogue, to
     * be read and compared before anything is added.
     */
    const open = (meditation: Meditation) =>
        isTheirs(meditation)
            ? navigate(`/meditations/${meditation.id}`)
            : setPreviewing(meditation.id);

    // The one thing kept in view: adding someone else's, or where it went.
    const actionFor = (meditation: Meditation) => {
        if (isTheirs(meditation)) return null;
        return meditation.my_copy_id ? (
            // Already taken: say so, and lead to their copy rather than
            // offering the same meditation a second time.
            <Button
                size="sm"
                variant="outline"
                startIcon={<Check size={15} />}
                onClick={() => navigate(`/meditations/${meditation.my_copy_id}`)}
            >
                {t("meditation.inLibrary")}
            </Button>
        ) : (
            <Button
                size="sm"
                variant="outline"
                startIcon={<BookmarkPlus size={15} />}
                loading={copyMeditation.isPending && copyMeditation.variables === meditation.id}
                onClick={() => void copy(meditation.id)}
            >
                {t("meditation.copyToLibrary")}
            </Button>
        );
    };

    const menuFor = (meditation: Meditation): MeditationMenuItem[] => {
        if (!isTheirs(meditation)) {
            return [
                {
                    label: t("meditation.preview.openPage"),
                    icon: ExternalLink,
                    onSelect: () => navigate(`/meditations/${meditation.id}`),
                },
            ];
        }
        const items: MeditationMenuItem[] = [
            {
                label: t("meditation.share.action"),
                icon: Share2,
                onSelect: () => setSharing({ id: meditation.id, title: meditation.title }),
            },
        ];
        if (tab === "mine" && meditation.is_owner && !meditation.in_center_library) {
            const isPublic = meditation.visibility === "public";
            items.push({
                label: t(isPublic ? "meditation.publish.withdraw" : "meditation.publish.menuPublish"),
                icon: isPublic ? Lock : Globe,
                disabled: unpublishMeditation.isPending,
                onSelect: () => togglePublished(meditation),
            });
        }
        return items;
    };

    const previewed = previewing
        ? meditations.find((meditation) => meditation.id === previewing)
        : undefined;

    // Everything narrowing the list but the order, which only sorts it.
    const narrowed = Object.entries(filters).some(([key, value]) => key !== "order" && value);
    const clearFilters = () => changeFilters({ order: filters.order });

    const layoutClasses = (value: Layout) =>
        `flex size-9 items-center justify-center rounded-md transition-colors ${
            layout === value
                ? "bg-surface-raised text-accent shadow-theme-xs"
                : "text-content-tertiary hover:text-content-secondary"
        }`;

    const tabClasses = (value: Tab) =>
        `min-h-11 rounded-lg px-4 text-sm font-medium transition-colors ${
            tab === value
                ? "bg-accent text-accent-contrast"
                : "text-content-secondary hover:bg-surface-sunken"
        }`;

    return (
        <>
            <PageMeta
                title={t("meditation.title")}
                description={t("meditation.toolShort")}
            />

            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-semibold text-content-primary">
                    {t("meditation.title")}
                </h1>
                <Button
                    startIcon={<Plus size={16} />}
                    onClick={() => navigate("/meditations/new")}
                >
                    {t("meditation.create")}
                </Button>
            </div>

            <div
                role="tablist"
                aria-label={t("meditation.title")}
                className="mb-5 inline-flex max-w-full flex-wrap gap-1 rounded-xl bg-surface-raised p-1"
            >
                {(
                    [
                        ["mine", "meditation.tabs.mine"],
                        ["public", "meditation.tabs.public"],
                        ...(inCenter ? [["center", "meditation.tabs.center"]] : []),
                    ] as [Tab, string][]
                ).map(([value, labelKey]) => (
                    <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={tab === value}
                        className={tabClasses(value)}
                        onClick={() => selectTab(value)}
                    >
                        {t(labelKey)}
                    </button>
                ))}
            </div>

            {/* What the catalogue holds: its meditations, or its audio tracks. */}
            <SectionTabs
                tabs={[
                    { id: "meditations", label: t("meditation.sections.meditations"), icon: Sparkles },
                    { id: "tracks", label: t("meditation.sections.tracks"), icon: Music },
                ]}
                activeTab={section}
                onChange={selectSection}
                label={t("meditation.sections.label")}
            />

            {section === "meditations" ? (
            <>
            <MeditationFilterBar
                filters={filters}
                onChange={changeFilters}
                ownTherapyTypes={ownLabels.therapyTypes}
                ownProblems={ownLabels.problems}
                showAuthor={tab !== "mine"}
                showOrder={tab === "public"}
            />

            {tab === "public" && (
                <p className="mb-4 rounded-xl border border-border-subtle bg-surface-raised px-4 py-3 text-sm text-content-secondary">
                    {t("meditation.publicNotice")}
                </p>
            )}

            {tab === "center" && (
                <div className="mb-4 flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-raised px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="flex items-start gap-2 text-sm text-content-secondary">
                        <Building2 size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                        {t(isAdmin ? "meditation.centerNoticeAdmin" : "meditation.centerNotice", {
                            center: center?.name,
                        })}
                    </p>
                    {isAdmin && (
                        <Button
                            size="sm"
                            startIcon={<Plus size={15} />}
                            onClick={() => navigate("/meditations/new?for=center")}
                        >
                            {t("meditation.createForCenter")}
                        </Button>
                    )}
                </div>
            )}

            {notAdopted ? (
                <p role="alert" className="text-sm text-content-secondary">
                    {t("meditation.notAdopted")}
                </p>
            ) : active.isLoading ? (
                <p className="flex items-center gap-2 text-sm text-content-secondary">
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                    {t("common.loading")}
                </p>
            ) : active.isError ? (
                <p role="alert" className="text-sm text-status-critical">
                    {t("meditation.loadFailed")}
                </p>
            ) : loaded.length === 0 && currentPage === 1 ? (
                /*
                 * An empty library says what to do next, not just that it
                 * is empty: start one, look at what others wrote, or undo
                 * the filters that emptied it.
                 */
                <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border-subtle px-6 py-10 text-center">
                    <span className="flex size-12 items-center justify-center rounded-full bg-accent-tint text-accent-on-tint">
                        {narrowed ? (
                            <SearchX size={20} aria-hidden="true" />
                        ) : (
                            <Sparkles size={20} aria-hidden="true" />
                        )}
                    </span>
                    <p className="max-w-md text-sm text-content-secondary">
                        {t(
                            narrowed
                                ? "meditation.emptyFiltered"
                                : tab === "mine"
                                  ? "meditation.emptyMine"
                                  : tab === "center"
                                    ? "meditation.emptyCenter"
                                    : "meditation.emptyPublic"
                        )}
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                        {narrowed ? (
                            <Button size="sm" variant="outline" onClick={clearFilters}>
                                {t("meditation.filters.clear")}
                            </Button>
                        ) : tab === "mine" ? (
                            <>
                                <Button
                                    size="sm"
                                    startIcon={<Plus size={15} />}
                                    onClick={() => navigate("/meditations/new")}
                                >
                                    {t("meditation.create")}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    startIcon={<Globe size={15} />}
                                    onClick={() => selectTab("public")}
                                >
                                    {t("meditation.explorePublic")}
                                </Button>
                            </>
                        ) : tab === "public" ? (
                            <Button size="sm" variant="outline" onClick={() => selectTab("mine")}>
                                {t("meditation.publishOneOfYours")}
                            </Button>
                        ) : null}
                    </div>
                </div>
            ) : (
                <>
                    <div
                        id="meditation-results"
                        className="mb-3 flex scroll-mt-4 items-center justify-between gap-3"
                    >
                        {/* The public library is never counted whole; it says its page instead. */}
                        <p className="text-sm text-content-tertiary" aria-live="polite">
                            {tab === "public"
                                ? t("meditation.pager.page", { page: currentPage })
                                : t("meditation.results", { count: loaded.length })}
                        </p>
                        <div
                            role="radiogroup"
                            aria-label={t("meditation.layout.label")}
                            className="flex w-fit gap-0.5 rounded-lg bg-surface-sunken p-0.5"
                        >
                            {(
                                [
                                    ["cards", LayoutGrid, "meditation.layout.cards"],
                                    ["list", List, "meditation.layout.list"],
                                ] as [Layout, typeof List, string][]
                            ).map(([value, Icon, labelKey]) => (
                                <button
                                    key={value}
                                    type="button"
                                    role="radio"
                                    aria-checked={layout === value}
                                    aria-label={t(labelKey)}
                                    title={t(labelKey)}
                                    className={layoutClasses(value)}
                                    onClick={() => selectLayout(value)}
                                >
                                    <Icon size={16} aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                    </div>
                    {actionError && (
                        <p role="alert" className="mb-3 text-sm text-status-critical">
                            {actionError}
                        </p>
                    )}
                    {layout === "cards" ? (
                        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {meditations.map((meditation) => (
                                <li key={meditation.id} className="[&>article]:h-full">
                                    <MeditationCard
                                        meditation={meditation}
                                        onOpen={() => open(meditation)}
                                        action={actionFor(meditation)}
                                        menu={menuFor(meditation)}
                                        footer={publishFormFor(meditation)}
                                    />
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className="flex flex-col gap-2">
                            {meditations.map((meditation) => (
                                <li key={meditation.id}>
                                    <MeditationRow
                                        meditation={meditation}
                                        onOpen={() => open(meditation)}
                                        action={actionFor(meditation)}
                                        menu={menuFor(meditation)}
                                        footer={publishFormFor(meditation)}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                    <MeditationPager
                        page={currentPage}
                        totalPages={totalPages}
                        hasNext={hasNext}
                        onChange={goToPage}
                    />
                </>
            )}

            </>
            ) : (
                /*
                 * The catalogue's audio tracks: the same piece goes under a
                 * dozen meditations, and it is uploaded once.
                 */
                <section aria-label={t("meditation.sections.tracks")}>
                    {tab === "public" && (
                        <p className="mb-4 rounded-xl border border-border-subtle bg-surface-raised px-4 py-3 text-sm text-content-secondary">
                            {t("meditation.tracks.publicNotice")}
                        </p>
                    )}
                    {tab === "center" && (
                        <p className="mb-4 flex items-start gap-2 rounded-xl border border-border-subtle bg-surface-raised px-4 py-3 text-sm text-content-secondary">
                            <Building2 size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                            {t(
                                isAdmin
                                    ? "meditation.tracks.centerNoticeAdmin"
                                    : "meditation.tracks.centerNotice",
                                { center: center?.name }
                            )}
                        </p>
                    )}
                    <MeditationTrackLibrary
                        key={tab}
                        scope={tab === "mine" ? "own" : tab}
                        canUpload={tab === "mine" || (tab === "center" && isAdmin)}
                        onExplore={() => selectTab("public")}
                        onShareYours={() => selectTab("mine")}
                    />
                </section>
            )}

            {previewed && (
                <MeditationPreview
                    meditation={previewed}
                    onClose={() => setPreviewing(null)}
                    adding={copyMeditation.isPending}
                    onAdd={() => void copy(previewed.id)}
                    onNavigate={(id) => navigate(`/meditations/${id}`)}
                />
            )}

            {sharing && (
                <MeditationShareSheet
                    meditationId={sharing.id}
                    title={sharing.title}
                    isOpen={true}
                    onClose={() => setSharing(null)}
                />
            )}
        </>
    );
}
